import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { CertificationDto } from "@/core/features/certifications/certification.dto";
import { CreateCertificationDto } from "@/core/features/certifications/create-certification.dto";
import { UpdateCertificationDto } from "@/core/features/certifications/update-certification.dto";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { IRequest } from "@/interfaces/IRequest";
import { OptionType } from "@/types/OptionType";
import { CertificationStatusEnum } from "@altertec_gparn/lib";
import { HttpRequest } from "@/core/common/http/http-request";
import { CreateFileDto } from "@/core/features/file/create-file.dto";
import { TimeOffDto } from "@/core/features/times-off/time-off.dto";
import { DeleteFileDto } from "@/core/features/file/delete-file.dto";
import { plainToInstance } from "class-transformer";
import { CreateCertificationWizardDto } from "@/core/features/certifications/create-certification-wizard.dto";
import { AddExpensesToCertificationDto } from "@/core/features/certifications/add-expenses-to-certification.dto";
import { UpdateCertificationStatusDto } from "@/core/features/certifications/update-certification-status.dto";
import { AddWorksheetsToCertificationDto } from "@/core/features/worksheets/add-worksheets-to-certification.dto";
import { cloneDeep } from "lodash";
import { CertificationEconomicAnalysisTotalDto } from "@/core/features/certification-economic-analyses/certification-economic-analysis-total.dto";
import { RemoveWorksheetsFromCertificationDto } from "@/core/features/worksheets/remove-worksheets-from-certification.dto";
import { AddOnCallsToCertificationDto } from "@/core/features/worksheets/add-on-calls-to-certification.dto";
import { RemoveOnCallsFromCertificationDto } from "@/core/features/worksheets/remove-on-calls-from-certification.dto";
import { RemoveExpensesFromCertificationDto } from "@/core/features/worksheets/remove-expenses-from-certification.dto";
import { SetCertificationsRegistrationDateBulkDto } from "@/core/features/certifications/set-certifications-registration-date-bulk.dto";
import { SetCertificationsRegistrationDateBulkResult } from "@/core/features/certifications/set-certifications-registration-date-bulk-result.interface";
import { LinkInvoicesToCertificationDto } from "@/core/features/certifications-have-invoices/link-invoices-to-certification.dto";
import { UpdateCertificationInvoiceAllocationDto } from "@/core/features/certifications-have-invoices/update-certification-invoice-allocation.dto";
import { CreateAndLinkInvoiceDto } from "@/core/features/certifications-have-invoices/create-and-link-invoice.dto";
import { InvoiceDto } from "@/core/features/invoice/invoice.dto";
import { RemoveInvoicesFromCertificationDto } from "@/core/features/certifications-have-invoices/remove-invoices-from-certification.dto";
import { UpdateCertificationInvoiceDto } from "@/core/features/certifications-have-invoices/update-certification-invoice.dto";
import { CertificationHasInvoiceDto } from "@/core/features/certifications-have-invoices/certification-has-invoice.dto";
import { CertificationValuationAccountingConfigurationDto } from "@/core/features/certifications/certification-valuation-accounting-configuration.dto";
import { UpdateCertificationValuationAccountingConfigurationDto } from "@/core/features/certifications/update-certification-valuation-accounting-configuration.dto";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class CertificationsResource extends ResourceBaseService<
  CertificationDto,
  CreateCertificationDto,
  UpdateCertificationDto
> {
  protected EntityDto = CertificationDto;
  protected CreateEntityDto = CreateCertificationDto;
  protected UpdateEntityDto = UpdateCertificationDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/certifications");
  }

  createManyAttachments(id: string, files: CreateFileDto[]): Promise<TimeOffDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "POST",
        auth: true,
        body: { bulk: files },
      })
    );
  }

  deleteAttachment(id: string, deleteFile: DeleteFileDto): Promise<TimeOffDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "DELETE",
        auth: true,
        body: deleteFile,
      })
    );
  }

  createWizard(createCertificationWizard: CreateCertificationWizardDto): Promise<CertificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/create-wizard",
        method: "POST",
        auth: true,
        body: createCertificationWizard,
      })
    );
  }

  async getCertificationsByProjectId(projectId: string, request: IRequest): Promise<FindManyResult<CertificationDto>> {
    const req = cloneDeep(request);
    if (!req.join) req.join = [];
    if (!req.join.includes("certificationHasProjects")) req.join.push("certificationHasProjects");

    if (!req.filters) req.filters = {};
    req.filters["certificationHasProjects.project"] = projectId;

    return this.findAll(req);
  }

  async optionLoader(inputValue: string): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "code"] } : {};
    request.sortBy = "code";
    const { data } = await this.findAll(applyOptionLoaderPagination(request));
    return data.map((value) => value.mapEntityToOptionType([value.code]));
  }

  changeStatus(id: string, status: CertificationStatusEnum): Promise<CertificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/change-status",
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateCertificationStatusDto, { certificationId: id, status: status }),
      })
    );
  }

  addExpenses(id: string, expenseIds: string[]): Promise<CertificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/add-expenses",
        method: "PATCH",
        auth: true,
        body: plainToInstance(AddExpensesToCertificationDto, { certificationId: id, expenseIds: expenseIds }),
      })
    );
  }

  async removeExpensesFromCertification(certificationId: string, expenseIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/remove-expenses",
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemoveExpensesFromCertificationDto, {
          certificationId: certificationId,
          expenseIds: expenseIds,
        }),
      })
    );
  }

  async addWorksheetsToCertification(certificationId: string, worksheetIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/add-worksheets",
        method: "PATCH",
        auth: true,
        body: plainToInstance(AddWorksheetsToCertificationDto, {
          certificationId: certificationId,
          worksheetIds: worksheetIds,
        }),
      })
    );
  }

  async removeWorksheetsFromCertification(certificationId: string, worksheetIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/remove-worksheets",
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemoveWorksheetsFromCertificationDto, {
          certificationId: certificationId,
          worksheetIds: worksheetIds,
        }),
      })
    );
  }

  async addOnCallsToCertification(certificationId: string, onCallIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/add-on-calls",
        method: "PATCH",
        auth: true,
        body: plainToInstance(AddOnCallsToCertificationDto, {
          certificationId: certificationId,
          onCallIds: onCallIds,
        }),
      })
    );
  }

  async removeOnCallsFromCertification(certificationId: string, onCallIds: string[]): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/remove-on-calls",
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemoveOnCallsFromCertificationDto, {
          certificationId: certificationId,
          onCallIds: onCallIds,
        }),
      })
    );
  }

  async deleteManyProjectsHaveCertifications(certificationId: string, projectHasCertificationIds: string[]) {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + certificationId + "/delete-many",
        method: "PATCH",
        auth: true,
        body: { certificationId, projectHasCertificationIds },
      })
    );
  }

  async getTotal(request: IRequest): Promise<CertificationEconomicAnalysisTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(CertificationEconomicAnalysisTotalDto, result));
  }

  async exportExcel(request: IRequest): Promise<{ jobId: string }> {
    return this.apiHttpClient.request<null, { jobId: string }>(
      HttpRequest.create({
        url: this.resourceUrl + "/excel/export",
        method: "GET",
        auth: true,
        query: this.getQueryBuilder(request).query(),
      })
    );
  }

  async setRegistrationDateInBulk(
    certificationIds: string[],
    registrationDate: string | Date
  ): Promise<SetCertificationsRegistrationDateBulkResult> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/bulk/registration-date",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetCertificationsRegistrationDateBulkDto, {
          certificationIds,
          registrationDate,
        }),
      })
    );
  }

  linkInvoices(body: LinkInvoicesToCertificationDto): Promise<CertificationHasInvoiceDto[]> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/add-invoices`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(LinkInvoicesToCertificationDto, body),
      })
    );
  }

  updateInvoiceAllocation(body: UpdateCertificationInvoiceAllocationDto): Promise<CertificationHasInvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/update-invoice-allocation`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateCertificationInvoiceAllocationDto, body),
      })
    );
  }

  updateInvoice(body: UpdateCertificationInvoiceDto): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/update-invoice`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateCertificationInvoiceDto, body),
      })
    );
  }

  createAndLinkInvoice(body: CreateAndLinkInvoiceDto): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/create-and-link-invoice`,
        method: "POST",
        auth: true,
        body: plainToInstance(CreateAndLinkInvoiceDto, body),
      })
    );
  }

  removeInvoices(body: RemoveInvoicesFromCertificationDto): Promise<{ success: boolean }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/remove-invoices`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(RemoveInvoicesFromCertificationDto, body),
      })
    );
  }

  async getValuationAccountingConfiguration(): Promise<CertificationValuationAccountingConfigurationDto[]> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/valuation-accounting-configuration`,
        method: "GET",
        auth: true,
      })
    );

    return plainToInstance(
      CertificationValuationAccountingConfigurationDto,
      result
    ) as unknown as CertificationValuationAccountingConfigurationDto[];
  }

  async saveValuationAccountingConfiguration(
    body: UpdateCertificationValuationAccountingConfigurationDto
  ): Promise<CertificationValuationAccountingConfigurationDto[]> {
    const result = await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/valuation-accounting-configuration`,
        method: "POST",
        auth: true,
        body: plainToInstance(UpdateCertificationValuationAccountingConfigurationDto, body),
      })
    );

    return plainToInstance(
      CertificationValuationAccountingConfigurationDto,
      result
    ) as unknown as CertificationValuationAccountingConfigurationDto[];
  }
}
